import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { fetchAllStaffWorkerSessions } from "./use-staff-worker-sessions";


const EMPTY_SESSIONS = [];
const STATUS_SESSION_PAGE_CONCURRENCY = 3;

const useStaffWorkerStatusSessions = ({ eeID, enabled = false }) => {
    const client = useApolloClient();
    const eventID = String(eeID ?? "").trim();
    const shouldLoad = Boolean(enabled && eventID);
    const [ attempt, setAttempt ] = useState(0);
    const context = useMemo(() => ({ client, eventID, shouldLoad, attempt }), [ client, eventID, shouldLoad, attempt ]);
    const [ state, setState ] = useState(null);
    const retry = useCallback(() => {
        if (shouldLoad) setAttempt((value) => value + 1);
    }, [ shouldLoad ]);

    useEffect(() => {
        let isCurrent = true;

        if (!shouldLoad)
        {
            setState(null);
            return undefined;
        }

        setState(null);
        fetchAllStaffWorkerSessions({
            client,
            where: { column: "EE_ID", operator: "EQ", value: eventID },
            pageConcurrency: STATUS_SESSION_PAGE_CONCURRENCY,
        }).then((sessions) => {
            if (isCurrent) setState({ context, sessions, error: null });
        }).catch((error) => {
            if (isCurrent) setState({ context, sessions: EMPTY_SESSIONS, error });
        });

        return () => { isCurrent = false; };
    }, [ client, context, eventID, shouldLoad ]);

    // Hide previous-event and disabled results during render, before effect cleanup.
    const currentState = shouldLoad && state?.context === context ? state : null;

    return {
        sessions: currentState?.sessions || EMPTY_SESSIONS,
        loading: shouldLoad && !currentState,
        error: currentState?.error || null,
        ready: Boolean(currentState && !currentState.error),
        retry,
    };
};

export default useStaffWorkerStatusSessions;
